import React, { useState, useEffect } from 'react';
import syncTemplatesService from '../services/syncTemplatesService';
import accountSyncTemplatesService from '../services/accountSyncTemplatesService';
import SyncTemplateGraphic from './SyncTemplateGraphic';
import './AdminSyncTemplates.css';
import './PartnerSyncTemplates.css';

// When accountId is provided the component enters chain-editing mode:
// tasks show active/inactive toggles and a Save button that persists to the DB.
// If a template has never been saved for this chain, all tasks default to active.

const SyncTemplatesReadOnly = ({ accountId }) => {
    const chainMode = !!accountId;

    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    // taskData[templateId] = { hasConfig: bool, tasks: [{...}] }
    const [taskData, setTaskData] = useState({});
    const [tasksLoading, setTasksLoading] = useState(null);

    // Pending local toggles before save: { [templateId]: { [taskId]: 'Y'|'N' } }
    const [pendingChanges, setPendingChanges] = useState({});
    const [saving, setSaving] = useState(null);
    const [saveError, setSaveError] = useState({});

    useEffect(() => {
        syncTemplatesService.listTemplates()
            .then(data => setTemplates(data || []))
            .catch(err => setError(err.response?.data?.message || err.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }, []);

    const loadTasks = async (t) => {
        setTasksLoading(t.id);
        try {
            if (chainMode) {
                const result = await accountSyncTemplatesService.getTasks(accountId, t.id);
                setTaskData(prev => ({ ...prev, [t.id]: result }));
            } else {
                const rows = await syncTemplatesService.listTasks(t.id);
                setTaskData(prev => ({ ...prev, [t.id]: { hasConfig: false, tasks: rows || [] } }));
            }
        } catch {
            setTaskData(prev => ({ ...prev, [t.id]: { hasConfig: false, tasks: [] } }));
        } finally {
            setTasksLoading(null);
        }
    };

    const handleToggle = async (t) => {
        if (expandedId === t.id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(t.id);
        if (!taskData[t.id]) await loadTasks(t);
    };

    const handleTaskToggle = (templateId, taskId, currentActive) => {
        const next = currentActive === 'Y' ? 'N' : 'Y';
        setPendingChanges(prev => ({
            ...prev,
            [templateId]: { ...(prev[templateId] || {}), [taskId]: next }
        }));
    };

    const getTaskActive = (templateId, task) => {
        const pending = pendingChanges[templateId];
        if (pending && pending[task.id] !== undefined) return pending[task.id];
        return task.active || 'Y';
    };

    const hasPendingChanges = (templateId) => {
        const pending = pendingChanges[templateId];
        return pending && Object.keys(pending).length > 0;
    };

    const handleSave = async (t) => {
        const data = taskData[t.id];
        if (!data) return;
        setSaving(t.id);
        setSaveError(prev => ({ ...prev, [t.id]: null }));
        try {
            const tasks = data.tasks.map(task => ({
                taskId: task.id,
                active: getTaskActive(t.id, task),
            }));
            const result = await accountSyncTemplatesService.saveTasks(accountId, t.id, tasks);
            setTaskData(prev => ({ ...prev, [t.id]: result }));
            setPendingChanges(prev => { const n = { ...prev }; delete n[t.id]; return n; });
        } catch (err) {
            setSaveError(prev => ({ ...prev, [t.id]: err.response?.data?.message || 'Save failed' }));
        } finally {
            setSaving(null);
        }
    };

    if (loading) return <div className="ast-loading">Loading sync templates…</div>;
    if (error) return <div className="ast-error">{error}</div>;

    return (
        <div className="ast-list" style={{ marginTop: 0 }}>
            {templates.length === 0 && (
                <div className="ast-empty">No sync templates found.</div>
            )}
            {templates.map(t => {
                const isOpen = expandedId === t.id;
                const data = taskData[t.id];
                const templateTasks = data?.tasks || [];
                const isConfigured = data?.hasConfig;
                const pendingSave = hasPendingChanges(t.id);

                return (
                    <div key={t.id} className="ast-template-card" style={{ cursor: 'pointer' }} onClick={() => handleToggle(t)}>
                        <div className="ast-template-row">
                            <div className="ast-template-info">
                                <SyncTemplateGraphic template={t} compact />
                                <div className="ast-template-meta">
                                    <span className="ast-template-name">{t.name}</span>
                                    {t.code && <span className="ast-template-code">{t.code}</span>}
                                    <span className={`ast-badge ast-badge-${(t.status || 'ACTIVE').toLowerCase()}`}>{t.status || 'ACTIVE'}</span>
                                    {t.description && <span className="ast-template-desc-inline">{t.description}</span>}
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                                {chainMode && isConfigured && (
                                    <span style={{ fontSize: '0.75rem', color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 10 }}>
                                        {templateTasks.filter(task => getTaskActive(t.id, task) === 'N').length === 0
                                            ? 'All tasks active'
                                            : `${templateTasks.filter(task => getTaskActive(t.id, task) === 'N').length} task(s) disabled`}
                                    </span>
                                )}
                                <svg
                                    width="16" height="16" viewBox="0 0 16 16" fill="none"
                                    style={{ flexShrink: 0, transition: 'transform 0.15s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', color: '#6b7280' }}
                                >
                                    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        </div>

                        {isOpen && (
                            <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }} onClick={e => e.stopPropagation()}>
                                {tasksLoading === t.id ? (
                                    <div className="ast-loading" style={{ padding: '0.5rem 0' }}>Loading tasks…</div>
                                ) : templateTasks.length === 0 ? (
                                    <div className="ast-no-tasks">No tasks defined.</div>
                                ) : (
                                    <>
                                        {chainMode && !isConfigured && (
                                            <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: '0.8rem', color: '#92400e' }}>
                                                No chain configuration saved yet — all tasks are active by default. Toggle tasks off and save to restrict access for hotels in this chain.
                                            </div>
                                        )}
                                        <table className="ast-tasks-table">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Name</th>
                                                    <th>Phase</th>
                                                    <th>Target</th>
                                                    <th>Auto</th>
                                                    <th>Repeatable</th>
                                                    {chainMode && <th>Active</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {templateTasks.map(task => {
                                                    const active = getTaskActive(t.id, task);
                                                    return (
                                                        <tr key={task.id} style={{ opacity: active === 'N' ? 0.5 : 1 }}>
                                                            <td>{task.sequence}</td>
                                                            <td>
                                                                <div>{task.name}</div>
                                                                {task.description && <div className="ast-task-desc">{task.description}</div>}
                                                            </td>
                                                            <td>{task.phase || '—'}</td>
                                                            <td>{task.target || '—'}</td>
                                                            <td>{task.automaticYn}</td>
                                                            <td>{task.repeatable}</td>
                                                            {chainMode && (
                                                                <td>
                                                                    <button
                                                                        className={`pst-toggle ${active === 'Y' ? 'pst-toggle-off' : 'pst-toggle-on'}`}
                                                                        onClick={() => handleTaskToggle(t.id, task.id, active)}
                                                                        title={active === 'Y' ? 'Click to disable for this chain' : 'Click to enable for this chain'}
                                                                    >
                                                                        {active === 'Y' ? 'Active' : 'Inactive'}
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>

                                        {chainMode && (
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem' }}>
                                                {saveError[t.id] && (
                                                    <span style={{ fontSize: '0.8rem', color: '#dc2626' }}>{saveError[t.id]}</span>
                                                )}
                                                <button
                                                    className="pp-rules-add-btn"
                                                    onClick={() => handleSave(t)}
                                                    disabled={saving === t.id}
                                                >
                                                    {saving === t.id ? (
                                                        <>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/><path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
                                                            Saving…
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                            {pendingSave ? 'Save Changes' : 'Save'}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default SyncTemplatesReadOnly;
